import { Download, Upload } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { useContext, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, RadioGroupOption } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { GeneralPageContext } from 'features/globalSetup/contexts/GeneralPageContext'
import { UploadDialog } from 'features/inventory/domain/forms/GeneralForm/sections/BulkManagement/components/UploadDialog/UploadDialog'
import { useScopeOptions } from 'features/inventory/domain/forms/GeneralForm/sections/BulkManagement/hooks/useScopeOptions'
import { useAbility } from 'providers/casl'
import { URL } from '../constants'
import { useDownloadAdUnitsConfiguration } from '../hooks/useDownloadAdUnitsConfiguration'
import { Schema } from '../schema'

export const BulkManagement = (): React.JSX.Element => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const { t } = useTranslation(['features/globalSetup', 'common'])
  const { control } = useFormContext<Schema>()
  const ability = useAbility()
  const bulkManagementScope = useWatch({ control, name: 'bulkManagementScope' })

  const {
    workspace: { id: workspaceId, name: workspaceName },
  } = useContext(GeneralPageContext)

  const cannotUploadBulkManagement: boolean = ability.cannot('update', 'GlobalSetupField-workspace')

  const { download, loading: loadingDownload } = useDownloadAdUnitsConfiguration({
    scope: bulkManagementScope,
    workspaceName,
  })

  const scopeOptions: RadioGroupOption[] = useScopeOptions()

  return (
    <>
      <Paper data-cy='section-bulk-management'>
        <SectionHeader
          subtitle={t('general.workspace.form.bulkManagement.header.subtitle')}
          subtitleEndAdornment={
            <Link
              newTab
              to={URL.BULK_MANAGEMENT}
            >
              {t('common:learnMore')}
            </Link>
          }
          title={t('general.workspace.form.bulkManagement.header.title')}
        />

        <GapBox />

        <BoxForm>
          <RadioGroup
            control={control}
            label={t('general.workspace.form.bulkManagement.parameterScope.title')}
            name='bulkManagementScope'
            options={scopeOptions}
            tooltip={{
              content: t('general.workspace.form.bulkManagement.parameterScope.tooltip'),
            }}
          />
        </BoxForm>

        <GapBox />

        <Stack
          direction='row'
          spacing={2}
        >
          <Button
            data-cy='bulk-management-download-button'
            disabled={loadingDownload}
            onClick={() => download(workspaceId)}
            startIcon={<Download />}
            variant='contained'
          >
            {t('general.workspace.form.bulkManagement.buttons.download')}
          </Button>

          <Button
            data-cy='bulk-management-upload-button'
            disabled={cannotUploadBulkManagement}
            onClick={() => void setIsDialogOpen(true)}
            startIcon={<Upload />}
            variant='contained'
          >
            {t('general.workspace.form.bulkManagement.buttons.upload')}
          </Button>
        </Stack>
      </Paper>

      <UploadDialog
        hint={t('general.workspace.form.bulkManagement.dialog.hint')}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        title={t('general.workspace.form.bulkManagement.dialog.title')}
        workspaceId={workspaceId}
      />
    </>
  )
}
